import { lazy, Suspense, useMemo, type ReactElement, type ReactNode } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getLandingEditorApiOrigin } from '../../lib/landing-editor-api';

const LazyCopilotKitBridge = lazy(async () => {
  const mod = await import('agentable-canvas/copilotkit-bridge');
  return { default: mod.CopilotKitBridge };
});

function isCopilotKitEnabled(): boolean {
  return (import.meta.env.VITE_ENABLE_COPILOTKIT ?? '').trim() === 'true';
}

/**
 * Opt-in CopilotKit provider (Stage 09). Lazy-loaded so the default host bundle
 * stays free of @copilotkit/react-core unless VITE_ENABLE_COPILOTKIT=true.
 */
export function OptionalCopilotKitBridge({ children }: { children: ReactNode }): ReactElement {
  const { session } = useAuth();

  const headers = useMemo(() => {
    const token = session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : undefined;
  }, [session?.access_token]);

  if (!isCopilotKitEnabled()) {
    return <>{children}</>;
  }

  const origin = getLandingEditorApiOrigin();
  const runtimeUrl = origin ? `${origin}/api/copilotkit` : undefined;

  return (
    <Suspense fallback={children}>
      <LazyCopilotKitBridge runtimeUrl={runtimeUrl} headers={headers}>
        {children}
      </LazyCopilotKitBridge>
    </Suspense>
  );
}
